import { FC, useMemo } from 'react'
import { Typography, Box } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useLeaseContractsMediaMetersList } from 'services/contracts/hooks/useLeaseContractsMediaMetersList'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'
import { useLanguage } from 'services/i18n/i18n.hooks'
import theme from 'styles/theme'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

export const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    y: {
      beginAtZero: true
    }
  }
}

interface AdditionalServicesAverageUtilityConsumptionProps {
  embedded?: boolean
}

const AdditionalServicesAverageUtilityConsumption: FC<
  AdditionalServicesAverageUtilityConsumptionProps
> = ({ embedded }) => {
  const { id } = useParams()
  const { leaseContractsMediaMetersList, isFetching, isLoading } = useLeaseContractsMediaMetersList(
    Number(id)
  )
  const { formatMessage } = useIntl()
  const { dateAdapter } = useLanguage()

  const data = useMemo(
    () => ({
      labels: leaseContractsMediaMetersList.map(({ dateMetersFrom }) =>
        dayjs(dateMetersFrom).locale(dateAdapter).format('MMMM YYYY')
      ),
      datasets: [
        {
          data: leaseContractsMediaMetersList.map(({ totalGrossCost }) => totalGrossCost),
          backgroundColor: '#527EF7',
          barThickness: 16
        }
      ]
    }),
    [leaseContractsMediaMetersList, dateAdapter]
  )

  if (isFetching || isLoading) {
    return null
  }

  if (!leaseContractsMediaMetersList.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '14px'
        }}>
        <Box
          sx={{
            width: '88px',
            height: '94px',
            borderRadius: '44px',
            backgroundColor: theme.r4r.greyBg,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            gap: '2px',
            pb: '24px'
          }}>
          <Box sx={{ width: '14px', height: '24px', backgroundColor: theme.r4r.navy }} />
          <Box sx={{ width: '14px', height: '36px', backgroundColor: theme.r4r.navy }} />
          <Box sx={{ width: '14px', height: '28px', backgroundColor: theme.r4r.navy }} />
        </Box>
        <Typography sx={{ fontSize: '13px', color: theme.r4r.grey, textAlign: 'center' }}>
          {formatMessage({
            id: 'additionalServicesAverageUtilityConsumption.noMedia',
            defaultMessage: 'Brak danych o zużyciu mediów.'
          })}
        </Typography>
      </Box>
    )
  }

  if (embedded) {
    return <Bar options={options} data={data} />
  }

  return (
    <Box
      sx={{
        border: `1px solid ${theme.r4r.greyLight}`,
        backgroundColor: '#fff',
        padding: '20px'
      }}>
      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: theme.r4r.grey, mb: '16px' }}>
        {formatMessage({
          id: 'additionalServicesAverageUtilityConsumption.media',
          defaultMessage: 'Średnie zużycie mediów (ms / pln)'
        })}
      </Typography>
      <Bar options={options} data={data} />
    </Box>
  )
}

export default AdditionalServicesAverageUtilityConsumption
